import Notification from '@rio-cloud/rio-uikit/Notification';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
    useDeleteSubscriptionMutation,
    useGetSubscriptionsQuery,
    usePostSubscriptionMutation,
} from '#api/notificationsApi.slice.ts';
import { useCreateTourMutation, useUpdateTourMutation } from '#api/tourApi.slice.ts';
import { createTourSubscriptions } from '#domain/createTourSubscriptions.ts';
import type { TourDetails } from '#domain/Tour.ts';
import type { TourStop } from '#domain/TourStop.ts';
import { useAppNavigate } from '#routes/AppNavLink.tsx';
import { routes } from '#routes/routes.ts';

import { ToBackendDTO, type TourFormValues } from '../model/NewTourFormState.ts';

type Options = {
    isOpen: boolean;
    tourToEdit?: TourDetails;
    sortableTourStops: TourStop[];
    onSaved: () => void;
};

export const useTourSave = ({ isOpen, tourToEdit, sortableTourStops, onSaved }: Options) => {
    const intl = useIntl();
    const navigate = useAppNavigate();
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [createTour, { isLoading: isLoadingCreateTour }] = useCreateTourMutation();
    const [updateTour, { isLoading: isLoadingUpdateTour }] = useUpdateTourMutation();
    const [createSubscription, { isLoading: isLoadingPostSubscription }] = usePostSubscriptionMutation();
    const [deleteSubscription, { isLoading: isLoadingDeleteSubscription }] = useDeleteSubscriptionMutation();
    const { data: existingSubscriptions, isLoading: isLoadingGetSubscriptions } = useGetSubscriptionsQuery(
        { tourId: tourToEdit?.id },
        { skip: !isOpen || !tourToEdit?.id }
    );
    const isLoading =
        isLoadingCreateTour ||
        isLoadingUpdateTour ||
        isLoadingPostSubscription ||
        isLoadingDeleteSubscription ||
        isLoadingGetSubscriptions;

    useEffect(() => {
        if (!isOpen || !tourToEdit) {
            setNotificationsEnabled(true);
            return;
        }

        if (existingSubscriptions) {
            setNotificationsEnabled(existingSubscriptions.items.length > 0);
        }
    }, [existingSubscriptions, isOpen, tourToEdit]);

    const createSubscriptions = (tourId: string) => createSubscription(createTourSubscriptions(tourId)).unwrap();

    const toSaveDTO = (payload: TourFormValues, trailerId?: string) =>
        ToBackendDTO(payload, sortableTourStops, trailerId);

    const deleteSubscriptions = async () => {
        const subscriptionsToDelete = existingSubscriptions?.items ?? [];
        const results = await Promise.allSettled(
            subscriptionsToDelete.map(subscription => deleteSubscription(subscription).unwrap())
        );
        const failedResult = results.find(result => result.status === 'rejected');
        if (failedResult?.status === 'rejected') {
            throw failedResult.reason;
        }
    };

    const syncSubscriptionsWithoutFailingTourSave = async (syncSubscriptions: () => Promise<unknown>) => {
        try {
            await syncSubscriptions();
        } catch (error) {
            console.error('Failed to synchronize tour subscriptions', error);
        }
    };

    const submitEditTour = async (payload: TourFormValues, tour: TourDetails) => {
        const updatedTour = await updateTour({
            id: tour.id,
            tour: toSaveDTO(payload, tour.trailer.id),
        }).unwrap();
        const hasExistingSubscriptions = (existingSubscriptions?.items.length ?? 0) > 0;
        await syncSubscriptionsWithoutFailingTourSave(async () => {
            if (notificationsEnabled && !hasExistingSubscriptions) {
                await createSubscriptions(updatedTour.id);
            } else if (!notificationsEnabled) {
                await deleteSubscriptions();
            }
        });
        Notification.success(
            intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.edit.success',
            })
        );
        onSaved();
        navigate(`${routes.TOURS}/${updatedTour.id}`);
    };

    const submitCreateTour = async (payload: TourFormValues) => {
        const createdTour = await createTour(toSaveDTO(payload)).unwrap();
        if (notificationsEnabled) {
            await syncSubscriptionsWithoutFailingTourSave(() => createSubscriptions(createdTour.id));
        }
        Notification.success(
            intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.create.success',
            })
        );
        onSaved();
        navigate(`${routes.TOURS}/${createdTour.id}`);
    };

    const submitTourPayload = async (payload: TourFormValues) => {
        try {
            if (tourToEdit) {
                await submitEditTour(payload, tourToEdit);
                return;
            }

            await submitCreateTour(payload);
        } catch {
            Notification.error(
                intl.formatMessage({
                    id: tourToEdit
                        ? 'intl-msg:ordercom-cloud-frontend.tours.edit.error'
                        : 'intl-msg:ordercom-cloud-frontend.tours.create.error',
                })
            );
        }
    };

    return { isLoading, notificationsEnabled, setNotificationsEnabled, submitTourPayload };
};
